import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {AudioRecorder} from 'react-audio-voice-recorder';
import {api} from './Api.js';
import styles from './css/Grammar.module.css';
import WordModalWindow from './WordModalWindow';
import GrammarStats from './GrammarStats';
import PreflightLoader, {parseISODuration} from './PreflightLoader';
import {config} from './Constants';

const exerciseDetails = {
    MULTIPLE_CHOICE: {label: 'Choose the answer', icon: 'format_list_bulleted'},
    FILL_IN_THE_BLANK: {label: 'Complete the sentence', icon: 'edit'},
    SENTENCE_WRITING: {label: 'Write a sentence', icon: 'short_text'},
    KEY_WORD_TRANSFORMATION: {label: 'Transform the sentence', icon: 'swap_horiz'},
    WORD_ORDER: {label: 'Put the words in order', icon: 'reorder'},
    ERROR_CORRECTION: {label: 'Correct the mistake', icon: 'spellcheck'},
    SITUATIONAL_RESPONSE: {label: 'Respond to the situation', icon: 'forum'},
    SPEECH_REPETITION: {label: 'Listen and repeat', icon: 'record_voice_over'}
};

const slideDetails = {
    INTRODUCTION: {label: 'Overview', icon: 'school'},
    USAGE: {label: 'When to use it', icon: 'tips_and_updates'},
    FORM: {label: 'Form', icon: 'account_tree'},
    EXAMPLES: {label: 'Examples', icon: 'format_quote'},
    CONTRAST: {label: 'Compare', icon: 'compare_arrows'},
    COMMON_MISTAKES: {label: 'Common mistakes', icon: 'error_outline'},
    SUMMARY: {label: 'Quick recap', icon: 'auto_awesome'}
};

const normaliseAnswer = (value) => String(value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .replace(/[‘’]/g, "'")
    .replace(/[.!?]+$/g, '')
    .trim();

const base64ToAudioUrl = (speech) => {
    if (!speech) return null;
    const bytes = Uint8Array.from(window.atob(speech), character => character.charCodeAt(0));
    return URL.createObjectURL(new Blob([bytes], {type: 'audio/mpeg'}));
};

const Grammar = ({showNotification}) => {
    const [round, setRound] = useState(null);
    const submittedRoundRef = useRef(false);
    const [screen, setScreen] = useState('lesson');
    const [slideIndex, setSlideIndex] = useState(0);
    const [expandedLesson, setExpandedLesson] = useState(null);
    const [loadingLesson, setLoadingLesson] = useState(false);
    const [exercisePosition, setExercisePosition] = useState(0);
    const [draftAnswer, setDraftAnswer] = useState('');
    const [submittedAnswer, setSubmittedAnswer] = useState('');
    const [answeredCorrectly, setAnsweredCorrectly] = useState(null);
    const [errorCount, setErrorCount] = useState(0);
    const [waiting, setWaiting] = useState(false);
    const [statistics, setStatistics] = useState(null);
    const [expectedWaitTime, setExpectedWaitTime] = useState(0);
    const [availableWords, setAvailableWords] = useState([]);
    const [orderedWords, setOrderedWords] = useState([]);
    const [audioUrl, setAudioUrl] = useState(null);
    const [transcribing, setTranscribing] = useState(false);
    const [hasSpeechTranscript, setHasSpeechTranscript] = useState(false);
    const [checking, setChecking] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [suggestedAnswer, setSuggestedAnswer] = useState(null);
    const [showWordModal, setShowWordModal] = useState(false);
    const [wordModalData, setWordModalData] = useState(null);
    const navigate = useNavigate();

    const exercises = useMemo(() => [...(round?.exercises || [])]
        .sort((first, second) => first.index - second.index), [round]);
    const slides = useMemo(() => [...(expandedLesson?.slides || round?.slides || [])]
        .sort((first, second) => first.position - second.position), [expandedLesson, round]);
    const slide = slides[slideIndex];
    const exercise = exercises[exercisePosition];
    const isFinished = screen === 'exercises' && exercisePosition >= exercises.length;
    const isLastSlide = slideIndex === slides.length - 1;
    const isCompactReview = Boolean(round?.review && !expandedLesson);

    const resetAnswer = useCallback(() => {
        setDraftAnswer('');
        setSubmittedAnswer('');
        setAnsweredCorrectly(null);
        setAvailableWords([]);
        setOrderedWords([]);
        setHasSpeechTranscript(false);
        setChecking(false);
        setFeedback(null);
        setSuggestedAnswer(null);
    }, []);

    const reset = useCallback(() => {
        setRound(null);
        submittedRoundRef.current = false;
        setScreen('lesson');
        setSlideIndex(0);
        setExpandedLesson(null);
        setLoadingLesson(false);
        setExercisePosition(0);
        setErrorCount(0);
        setWaiting(false);
        setStatistics(null);
        setExpectedWaitTime(0);
        resetAnswer();
    }, [resetAnswer]);

    const fetchRound = useCallback(async () => {
        setWaiting(true);
        if (config.enablePreflight) {
            const waitResponse = await api.fetchWaitTime('/grammar/round', navigate, showNotification);
            if (api.isValidResponse(waitResponse)) {
                setExpectedWaitTime(parseISODuration(waitResponse.data.expectedWaitTime));
            }
        }
        const response = await api.fetchGrammarRound(navigate, showNotification);
        if (api.isValidResponse(response)) {
            setRound(response.data);
        }
        setWaiting(false);
        setExpectedWaitTime(0);
    }, [navigate, showNotification]);

    const submitRound = useCallback(async (finalErrorCount = errorCount) => {
        if (!round || submittedRoundRef.current) return;
        submittedRoundRef.current = true;
        setWaiting(true);
        const response = await api.grammarRoundResult({
            planId: round.planId,
            errorCount: finalErrorCount
        }, navigate, showNotification);
        if (api.isValidResponse(response)) {
            setStatistics(response.data);
        }
        setWaiting(false);
    }, [errorCount, navigate, round, showNotification]);

    const moveToNextExercise = useCallback((finalErrorCount) => {
        const nextPosition = exercisePosition + 1;
        setExercisePosition(nextPosition);
        resetAnswer();
        if (nextPosition >= exercises.length) {
            submitRound(finalErrorCount);
        }
    }, [exercisePosition, exercises.length, resetAnswer, submitRound]);

    const showFullLesson = useCallback(async () => {
        if (!round?.planId || loadingLesson) return;
        setLoadingLesson(true);
        const response = await api.fetchGrammarLesson(round.planId, navigate, showNotification);
        if (api.isValidResponse(response)) {
            setExpandedLesson(response.data);
            setSlideIndex(0);
        }
        setLoadingLesson(false);
    }, [loadingLesson, navigate, round?.planId, showNotification]);

    const openExercises = useCallback(() => {
        if (!isLastSlide) return;
        setScreen('exercises');
        setExercisePosition(0);
        resetAnswer();
    }, [isLastSlide, resetAnswer]);

    const checkSituation = useCallback(async (value) => {
        if (!exercise || checking || answeredCorrectly !== null) return;
        setChecking(true);
        const response = await api.checkGrammarSituation(exercise.exerciseId, value, navigate, showNotification);
        if (api.isValidResponse(response)) {
            const result = response.data;
            setDraftAnswer(value);
            setSubmittedAnswer(value);
            setAnsweredCorrectly(result.accepted);
            setFeedback(result.feedback || null);
            setSuggestedAnswer(result.suggestedAnswer || null);
            if (!result.accepted) {
                setErrorCount(currentCount => currentCount + 1);
            }
        }
        setChecking(false);
    }, [answeredCorrectly, checking, exercise, navigate, showNotification]);

    const checkAnswer = useCallback((value) => {
        if (!exercise || answeredCorrectly !== null) return;
        const learnerAnswer = String(value || '').trim();
        if (!learnerAnswer) return;

        if (exercise.answerCheckType === 'AI') {
            checkSituation(learnerAnswer);
            return;
        }

        const isCorrect = normaliseAnswer(learnerAnswer) === normaliseAnswer(exercise.correctAnswer);
        setDraftAnswer(learnerAnswer);
        setSubmittedAnswer(learnerAnswer);
        setAnsweredCorrectly(isCorrect);
        if (!isCorrect) {
            setErrorCount(currentCount => currentCount + 1);
        }
    }, [answeredCorrectly, checkSituation, exercise]);

    const submitTextAnswer = useCallback(() => checkAnswer(draftAnswer), [checkAnswer, draftAnswer]);

    const markAsUnknown = useCallback(() => {
        if (!exercise || answeredCorrectly !== null || checking) return;
        setSubmittedAnswer('');
        setAnsweredCorrectly(false);
        setFeedback('No answer submitted. Review the model answer and the rule below.');
        setSuggestedAnswer(null);
        setErrorCount(currentCount => currentCount + 1);
    }, [answeredCorrectly, checking, exercise]);

    const markAsKnown = useCallback(() => {
        if (answeredCorrectly !== false) return;
        const correctedErrorCount = Math.max(0, errorCount - 1);
        setErrorCount(correctedErrorCount);
        moveToNextExercise(correctedErrorCount);
    }, [answeredCorrectly, errorCount, moveToNextExercise]);

    const recordSpeech = useCallback(async (blob) => {
        if (!exercise) return;
        setTranscribing(true);
        setExpectedWaitTime(0);
        if (config.enablePreflight) {
            const waitResponse = await api.fetchWaitTime('/grammar/speech/transcription', navigate, showNotification,
                {size: blob.size});
            if (api.isValidResponse(waitResponse)) {
                setExpectedWaitTime(parseISODuration(waitResponse.data.expectedWaitTime));
            }
        }
        const recording = new File([blob], 'grammar-response.webm', {type: blob.type || 'audio/webm'});
        const response = await api.transcribeGrammarExercise(exercise.exerciseId, recording, navigate, showNotification);
        if (api.isValidResponse(response)) {
            const transcription = response.data?.transcription ?? response.data;
            setDraftAnswer(transcription || '');
            setHasSpeechTranscript(true);
        }
        setTranscribing(false);
        setExpectedWaitTime(0);
    }, [exercise, navigate, showNotification]);

    useEffect(() => {
        if (round === null && !waiting) {
            fetchRound();
        }
    }, [fetchRound, round, waiting]);

    useEffect(() => {
        if (exercise?.type !== 'WORD_ORDER') return;
        setAvailableWords((exercise.wordBank || []).map((word, index) => ({id: `${index}-${word}`, word})));
        setOrderedWords([]);
    }, [exercise]);

    useEffect(() => {
        if (exercise?.type !== 'SPEECH_REPETITION' || !exercise.speech) {
            setAudioUrl(null);
            return undefined;
        }
        const url = base64ToAudioUrl(exercise.speech);
        setAudioUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [exercise]);

    const renderInteractiveText = useCallback((text) => {
        if (!text) return null;
        const stopWords = new Set(['the', 'and', 'but', 'for', 'are', 'was', 'were', 'been', 'being', 'this', 'that', 'these', 'those']);
        return text.split(/(\s+|[.,!?;:]|\b___+\b)/).map((segment, index) => {
            const isWord = /^[a-zA-Zа-яА-ЯёЁčćžšđČĆŽŠĐ]{3,}$/.test(segment);
            if (isWord && !stopWords.has(segment.toLowerCase())) {
                return <button className={styles.word} key={`word-${index}`} type="button" onClick={() => {
                    setWordModalData({text, wordEn: segment});
                    setShowWordModal(true);
                }}>{segment}</button>;
            }
            return <React.Fragment key={`text-${index}`}>{segment}</React.Fragment>;
        });
    }, []);

    const renderTextInput = (multiline = false, placeholder = 'Type your answer in English') => {
        const Input = multiline ? 'textarea' : 'input';
        return <div className={styles.answerForm}>
            <Input className={styles.textInput} value={draftAnswer} disabled={answeredCorrectly !== null || checking}
                   onChange={(event) => setDraftAnswer(event.target.value)} onKeyDown={(event) => {
                if (!multiline && event.key === 'Enter') submitTextAnswer();
            }} placeholder={placeholder} rows={multiline ? 3 : undefined}/>
            {answeredCorrectly === null && <button type="button" className="btn-large blue waves-effect waves-light"
                                                   onClick={submitTextAnswer} disabled={!draftAnswer.trim() || checking}>
                {checking ? 'Checking…' : 'Check'} <i className="material-icons right">check</i>
            </button>}
        </div>;
    };

    const renderInputGuide = () => {
        if (!['FILL_IN_THE_BLANK', 'SENTENCE_WRITING'].includes(exercise.type) || !exercise.inputGuide?.length) {
            return null;
        }
        return <div className={styles.inputGuide}>
            <span className={styles.guideLabel}>Use these components in order</span>
            <div>{exercise.inputGuide.map((hint, index) => <span className={styles.guideToken} key={`${index}-${hint}`}>{hint}</span>)}</div>
        </div>;
    };

    const renderWordOrder = () => <div className={styles.wordOrder}>
        <p className={styles.helperText}>Tap words to build the sentence. Tap a selected word to return it.</p>
        <div className={`${styles.wordTray} ${styles.answerTray}`}>
            {orderedWords.length === 0 && <span className={styles.placeholder}>Your sentence will appear here</span>}
            {orderedWords.map((token) => <button type="button" className={styles.wordToken} key={token.id}
                                                 disabled={answeredCorrectly !== null} onClick={() => {
                setOrderedWords(current => current.filter(item => item.id !== token.id));
                setAvailableWords(current => [...current, token]);
            }}>{token.word}</button>)}
        </div>
        <div className={styles.wordTray}>
            {availableWords.map((token) => <button type="button" className={styles.wordToken} key={token.id}
                                                   disabled={answeredCorrectly !== null} onClick={() => {
                setAvailableWords(current => current.filter(item => item.id !== token.id));
                setOrderedWords(current => [...current, token]);
            }}>{token.word}</button>)}
        </div>
        {answeredCorrectly === null && <button type="button" className="btn-large blue waves-effect waves-light"
                                               onClick={() => checkAnswer(orderedWords.map(token => token.word).join(' '))}
                                               disabled={orderedWords.length === 0}>Check <i className="material-icons right">check</i></button>}
    </div>;

    const renderSpeechRepetition = () => <div className={styles.speechPanel}>
        <div className={styles.audioPanel}>
            <i className="material-icons blue-text">record_voice_over</i><span>Listen to the target sentence, then repeat it aloud.</span>
            {audioUrl && <audio controls src={audioUrl} className={styles.audio}/>}
        </div>
        <p className={styles.helperText}>Record your repetition. We will show the transcription before checking it.</p>
        {transcribing ? <div className={styles.transcribing}><PreflightLoader expectedWaitTime={expectedWaitTime}/></div> :
            answeredCorrectly === null && <AudioRecorder onRecordingComplete={recordSpeech}
                                                         onNotAllowedOrFound={() => showNotification('Please allow microphone access.')}
                                                         audioTrackConstraints={{noiseSuppression: true, echoCancellation: true}}
                                                         showVisualizer={true}
            />}
        {hasSpeechTranscript && <div className={styles.transcript}>
            <span className={styles.transcriptLabel}>We heard</span>
            {renderTextInput(false, 'Your transcription will appear here')}
            <p className={styles.helperText}>You can edit the text if the transcription missed a word.</p>
            {answeredCorrectly === null && <button type="button" className={styles.retryRecording} onClick={() => {
                setDraftAnswer('');
                setHasSpeechTranscript(false);
            }}><i className="material-icons tiny">replay</i> Record again</button>}
        </div>}
    </div>;

    const renderExerciseInteraction = () => {
        switch (exercise.type) {
            case 'MULTIPLE_CHOICE':
                return <div className={styles.options}>{(exercise.options || []).map((option, index) => <button type="button"
                                                                                                                key={`${index}-${option}`}
                                                                                                                className={`${styles.option} ${submittedAnswer === option ? styles.selectedOption : ''}`}
                                                                                                                disabled={answeredCorrectly !== null}
                                                                                                                onClick={() => checkAnswer(option)}>{option || '_'}</button>)}</div>;
            case 'WORD_ORDER':
                return renderWordOrder();
            case 'SPEECH_REPETITION':
                return renderSpeechRepetition();
            case 'SENTENCE_WRITING':
            case 'SITUATIONAL_RESPONSE':
                return renderTextInput(true);
            default:
                return renderTextInput();
        }
    };

    const renderExerciseActions = () => <div className={styles.learnerActions}>
        {answeredCorrectly === null && exercise.type !== 'MULTIPLE_CHOICE' && <button type="button"
                                                                                      className="btn-large grey lighten-1 waves-effect waves-light"
                                                                                      disabled={checking} onClick={markAsUnknown}>
            <i className="material-icons left">help_outline</i>I don't know
        </button>}
        {answeredCorrectly === false && <button type="button" className="btn-large grey darken-1 waves-effect waves-light"
                                                onClick={markAsKnown}>
            <i className="material-icons left">thumb_up</i>I was right
        </button>}
    </div>;

    const renderFeedback = () => answeredCorrectly !== null && <>
        <div className={`${styles.feedback} ${answeredCorrectly ? styles.correct : styles.incorrect}`}>
            <i className="material-icons">{answeredCorrectly ? 'check_circle' : 'highlight_off'}</i>
            <div><strong>{answeredCorrectly ? 'Correct — nice work!' : 'Not quite.'}</strong>
                {!answeredCorrectly && feedback && <span>{feedback}</span>}
                {!answeredCorrectly && exercise.answerCheckType !== 'AI' && <span>The expected answer is <b>{exercise.correctAnswer}</b>.</span>}
                {!answeredCorrectly && suggestedAnswer && <span>The suggested answer is <b>{suggestedAnswer}</b>.</span>}
            </div>
        </div>
        {exercise.explanation && <div className={styles.explanation}>
            <div className={styles.explanationTitle}><i className="material-icons tiny">lightbulb_outline</i> Why?</div>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{exercise.explanation}</ReactMarkdown>
        </div>}
        <div className={styles.continue}>
            <button type="button" className="btn-large blue waves-effect waves-light"
                    onClick={() => moveToNextExercise()}>Continue <i className="material-icons right">arrow_forward</i></button>
        </div>
    </>;

    if ((waiting || round === null) && screen === 'lesson') {
        return <div className="common-preloader-form"><PreflightLoader expectedWaitTime={expectedWaitTime}/></div>;
    }

    if (!round) return null;

    return <div className={`container ${styles.grammarPage}`}>
        {showWordModal && <div className={styles.modalBackdrop}><WordModalWindow wordEn={wordModalData.wordEn}
                                                                                 text={wordModalData.text} onClose={() => setShowWordModal(false)} showNotification={showNotification}
                                                                                 navigate={navigate}/></div>}
        <div className="row">
            <div className="col s12 m10 offset-m1 l8 offset-l2">
                <div className={`card ${styles.grammarCard}`}>
                    <div className={`card-content ${styles.grammarCardContent}`}>
                        {screen === 'lesson' && slide && <section className={styles.lesson}>
                            <span className={styles.eyebrow}>{round.review ? 'Grammar review' : 'Grammar lesson'}</span>
                            <h4>{renderInteractiveText(round.topic)}</h4>
                            <div className={styles.progressHeader}>
                                <span>{slideDetails[slide.type]?.label || 'Lesson content'}</span>
                                <span>{slideIndex + 1} / {slides.length}</span>
                            </div>
                            <div className={styles.progress}>
                                <div style={{width: `${((slideIndex + 1) / slides.length) * 100}%`}}/>
                            </div>
                            <article className={`${styles.lessonText} ${styles[`slide${slide.type}`] || ''}`}>
                                <div className={styles.typeLabel}>
                                    <i className="material-icons tiny">{slideDetails[slide.type]?.icon || 'menu_book'}</i>
                                    {slideDetails[slide.type]?.label || 'Lesson content'}
                                </div>
                                <h5>{slide.title}</h5>
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{slide.content}</ReactMarkdown>
                                {slide.examples?.length > 0 && <div className={styles.slideExamples}>
                                    <span className={styles.examplesLabel}>Examples</span>
                                    {slide.examples.map((example, index) => {
                                        const isIncorrect = /^(incorrect|wrong|mistake|❌)\b/i.test(example.trim());
                                        const isCorrect = /^(correct|right|✓)/i.test(example.trim());
                                        const exampleStyle = isIncorrect ? styles.incorrectExample : isCorrect ? styles.correctExample : '';
                                        return <div className={`${styles.slideExample} ${exampleStyle}`} key={`${index}-${example}`}>
                                            {isIncorrect && <i className="material-icons tiny">close</i>}
                                            {isCorrect && <i className="material-icons tiny">check</i>}
                                            <span>{example}</span>
                                        </div>;
                                    })}
                                </div>}
                            </article>
                            {isCompactReview && <div className={styles.reviewActions}>
                                <button type="button" className={styles.secondaryAction} onClick={showFullLesson} disabled={loadingLesson}>
                                    {loadingLesson ? 'Loading lesson…' : 'Open full lesson'} <i className="material-icons right">menu_book</i>
                                </button>
                            </div>}
                            {expandedLesson && round.review && <div className={styles.reviewActions}>
                                <button type="button" className={styles.secondaryAction} onClick={() => {
                                    setExpandedLesson(null);
                                    setSlideIndex(0);
                                }}>Back to review <i className="material-icons right">short_text</i></button>
                            </div>}
                            <div className={styles.slideNavigation}>
                                <button type="button" className={styles.backButton} disabled={slideIndex === 0}
                                        onClick={() => setSlideIndex(currentIndex => Math.max(0, currentIndex - 1))}>
                                    <i className="material-icons left">arrow_back</i>Back
                                </button>
                                {isLastSlide ? <button type="button" className="btn-large blue waves-effect waves-light" onClick={openExercises}>
                                    Start exercises <i className="material-icons right">play_arrow</i>
                                </button> : <button type="button" className="btn-large blue waves-effect waves-light"
                                                    onClick={() => setSlideIndex(currentIndex => Math.min(slides.length - 1, currentIndex + 1))}>
                                    Next <i className="material-icons right">arrow_forward</i>
                                </button>}
                            </div>
                        </section>}
                        {isFinished && (waiting || !statistics) && <div className={styles.saving}><PreflightLoader expectedWaitTime={0}/></div>}
                        {isFinished && !waiting && statistics && <section className={styles.finished}>
                            <i className="material-icons">stars</i><h4>Practice complete!</h4><p>You completed all {exercises.length} exercises on {round.topic}.</p>
                            <GrammarStats grammar={statistics}/>
                            <div className={styles.finishedActions}>
                                <button type="button" className="btn-large blue waves-effect waves-light" onClick={reset}>Next topic <i className="material-icons right">refresh</i></button>
                                <button type="button" className="btn-large grey waves-effect waves-light" onClick={() => navigate('/')}>Dashboard <i className="material-icons right">home</i></button>
                            </div>
                        </section>}
                        {screen === 'exercises' && !isFinished && exercise && <section className={styles.exercise}>
                            <div className={styles.progressHeader}>
                                <span>Exercise {exercisePosition + 1} of {exercises.length}</span><span>{Math.round(((exercisePosition + 1) / exercises.length) * 100)}%</span></div>
                            <div className={styles.progress}>
                                <div style={{width: `${((exercisePosition + 1) / exercises.length) * 100}%`}}/>
                            </div>
                            <div className={styles.typeLabel}><i
                                className="material-icons tiny">{exerciseDetails[exercise.type]?.icon || 'assignment'}</i>{exerciseDetails[exercise.type]?.label || 'Grammar practice'}</div>
                            <h5>{renderInteractiveText(exercise.prompt)}</h5>
                            {exercise.context && <div className={styles.context}>{renderInteractiveText(exercise.context)}</div>}
                            {renderInputGuide()}
                            <div className={styles.interaction}>{renderExerciseInteraction()}</div>
                            {renderExerciseActions()}
                            {renderFeedback()}
                        </section>}
                    </div>
                </div>
            </div>
        </div>
    </div>;
};

export default Grammar;
